import { useEffect, useRef } from "react";

/**
 * GravityCanvas — Rev 25.2 verbatim from docs/NEXIRT_DESIGN/Home.html
 * (lines 847-1008). Rev 25.1 shipped a dot-field approximation that
 * drifted from the design's connected line-lattice + cursor-proximity
 * intersection dots; this rewrite copies the design's physics + render
 * loop without interpretation.
 *
 * Constants (verbatim):
 *   SPACING = 38  (grid spacing in css px)
 *   RADIUS  = 240 (gravity radius)
 *   STRENGTH= 1.2 (pull strength)
 *   spring  = 0.18 acceleration
 *   damping = 0.62 velocity
 *   fall-off = (1 - d/RADIUS)² × STRENGTH × 28 pixels of pull
 *   line alpha = rgba(255,255,255,0.045)
 *   intersection-dot proximity = RADIUS * 0.9
 *   color gradient: violet (107,79,230) → cyan (34,211,238) by t
 *   dot radius = 1.6 + t * 1.4
 *   dot alpha  = min(0.9, t² × 1.1)
 *
 * Mouse listeners attach to the parent .hero element (matches design),
 * NOT the canvas or window — keeps the field calm when the cursor leaves
 * the hero region.
 *
 * Reduced-motion: render the line lattice ONCE statically, no rAF loop,
 * no listeners (resize still redraws). Per the design's reduced branch
 * (Home.html lines 994-1004).
 */
export function GravityCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const parent = canvas.parentElement;
    if (!parent) return;

    const SPACING = 38;
    const RADIUS = 240;
    const STRENGTH = 1.2;
    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));

    type Pt = { bx: number; by: number; x: number; y: number; vx: number; vy: number };
    let W = 0;
    let H = 0;
    let cols = 0;
    let rows = 0;
    let pts: Pt[] = [];
    let raf = 0;
    const mouse = { x: -9999, y: -9999, active: false };

    function resize() {
      const rect = parent!.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      canvas!.width = W * dpr;
      canvas!.height = H * dpr;
      canvas!.style.width = `${W}px`;
      canvas!.style.height = `${H}px`;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(W / SPACING) + 2;
      rows = Math.ceil(H / SPACING) + 2;
    }

    function buildPts() {
      pts = [];
      for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
          const bx = i * SPACING - SPACING;
          const by = j * SPACING - SPACING;
          pts.push({ bx, by, x: bx, y: by, vx: 0, vy: 0 });
        }
      }
    }

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    function drawStatic() {
      ctx!.clearRect(0, 0, W, H);
      ctx!.strokeStyle = "rgba(255,255,255,0.045)";
      ctx!.lineWidth = 1;
      for (let j = 0; j < rows; j++) {
        ctx!.beginPath();
        for (let i = 0; i < cols; i++) {
          const p = pts[j * cols + i]!;
          if (i === 0) ctx!.moveTo(p.bx, p.by);
          else ctx!.lineTo(p.bx, p.by);
        }
        ctx!.stroke();
      }
      for (let i = 0; i < cols; i++) {
        ctx!.beginPath();
        for (let j = 0; j < rows; j++) {
          const p = pts[j * cols + i]!;
          if (j === 0) ctx!.moveTo(p.bx, p.by);
          else ctx!.lineTo(p.bx, p.by);
        }
        ctx!.stroke();
      }
    }

    function step() {
      const mx = mouse.x;
      const my = mouse.y;
      const active = mouse.active;

      for (const p of pts) {
        let tx = p.bx;
        let ty = p.by;
        if (active) {
          const dx = p.bx - mx;
          const dy = p.by - my;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < RADIUS && d > 0.0001) {
            const fall = 1 - d / RADIUS;
            const pull = fall * fall * STRENGTH * 28;
            tx = p.bx - (dx / d) * pull;
            ty = p.by - (dy / d) * pull;
          }
        }
        const ax = (tx - p.x) * 0.18;
        const ay = (ty - p.y) * 0.18;
        p.vx = (p.vx + ax) * 0.62;
        p.vy = (p.vy + ay) * 0.62;
        p.x += p.vx;
        p.y += p.vy;
      }

      ctx!.clearRect(0, 0, W, H);
      ctx!.lineWidth = 1;
      ctx!.strokeStyle = "rgba(255,255,255,0.045)";

      // horizontal lines
      for (let j = 0; j < rows; j++) {
        ctx!.beginPath();
        for (let i = 0; i < cols; i++) {
          const p = pts[j * cols + i]!;
          if (i === 0) ctx!.moveTo(p.x, p.y);
          else ctx!.lineTo(p.x, p.y);
        }
        ctx!.stroke();
      }
      // vertical lines
      for (let i = 0; i < cols; i++) {
        ctx!.beginPath();
        for (let j = 0; j < rows; j++) {
          const p = pts[j * cols + i]!;
          if (j === 0) ctx!.moveTo(p.x, p.y);
          else ctx!.lineTo(p.x, p.y);
        }
        ctx!.stroke();
      }

      if (active) {
        // intersection dots within RADIUS*0.9, violet → cyan by proximity
        for (const p of pts) {
          const dx = p.x - mx;
          const dy = p.y - my;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < RADIUS * 0.9) {
            const t = 1 - d / (RADIUS * 0.9);
            const alpha = Math.min(0.9, t * t * 1.1);
            const r1 = 107 + (34 - 107) * t;
            const g1 = 79 + (211 - 79) * t;
            const b1 = 230 + (238 - 230) * t;
            ctx!.fillStyle = `rgba(${r1 | 0},${g1 | 0},${b1 | 0},${alpha})`;
            ctx!.beginPath();
            ctx!.arc(p.x, p.y, 1.6 + t * 1.4, 0, Math.PI * 2);
            ctx!.fill();
          }
        }
        // soft violet glow around cursor
        const grad = ctx!.createRadialGradient(mx, my, 0, mx, my, RADIUS);
        grad.addColorStop(0, "rgba(107,79,230,0.18)");
        grad.addColorStop(0.5, "rgba(107,79,230,0.06)");
        grad.addColorStop(1, "rgba(107,79,230,0)");
        ctx!.fillStyle = grad;
        ctx!.fillRect(mx - RADIUS, my - RADIUS, RADIUS * 2, RADIUS * 2);
      }

      raf = requestAnimationFrame(step);
    }

    resize();
    buildPts();

    function onResize() {
      resize();
      buildPts();
      if (reduced) drawStatic();
    }
    window.addEventListener("resize", onResize);

    if (reduced) {
      drawStatic();
      return () => {
        window.removeEventListener("resize", onResize);
      };
    }

    // Mouse + touch on the parent element (per design — keeps the field
    // calm when the cursor leaves the hero).
    function onMove(e: MouseEvent) {
      const r = canvas!.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
      mouse.active = true;
    }
    function onLeave() {
      mouse.active = false;
      mouse.x = -9999;
      mouse.y = -9999;
    }
    function onTouch(e: TouchEvent) {
      const t = e.touches[0];
      if (!t) return;
      const r = canvas!.getBoundingClientRect();
      mouse.x = t.clientX - r.left;
      mouse.y = t.clientY - r.top;
      mouse.active = true;
    }

    parent.addEventListener("mousemove", onMove);
    parent.addEventListener("mouseleave", onLeave);
    parent.addEventListener("touchmove", onTouch, { passive: true });
    raf = requestAnimationFrame(step);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      parent.removeEventListener("mousemove", onMove);
      parent.removeEventListener("mouseleave", onLeave);
      parent.removeEventListener("touchmove", onTouch);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-gravity-canvas
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ zIndex: 0 }}
    />
  );
}

export default GravityCanvas;
